import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Button, Dialog } from './ui';

export interface ConfirmOptions {
  title: string;
  /** Erklärt die Folgen der Aktion. */
  message: ReactNode;
  /** Beschriftung des Bestätigen-Knopfs, z. B. „Ja, entfernen“. */
  confirmLabel?: string;
  cancelLabel?: string;
  /** „danger“ färbt den Bestätigen-Knopf rot (Standard: neutral). */
  tone?: 'danger' | 'default';
}

/**
 * Rückfrage vor folgenreichen Aktionen.
 *
 * `confirm(...)` liefert ein Promise, das mit `true` (bestätigt) oder `false`
 * (abgebrochen, Escape, Klick daneben) aufgelöst wird. Das Element
 * `confirmDialog` muss einmal im Baum der Komponente stehen.
 *
 * const { confirm, confirmDialog } = useConfirm();
 * if (!(await confirm({ title: '…', message: '…', tone: 'danger' }))) return;
 */
export function useConfirm(): { confirm: (options: ConfirmOptions) => Promise<boolean>; confirmDialog: JSX.Element } {
  const [state, setState] = useState<{ options: ConfirmOptions; resolve: (value: boolean) => void } | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  const confirm = useCallback(
    (options: ConfirmOptions): Promise<boolean> =>
      new Promise<boolean>((resolve) => {
        // Eine noch offene Rückfrage gilt als abgebrochen.
        stateRef.current?.resolve(false);
        setState({ options, resolve });
      }),
    [],
  );

  // Wird die Komponente abgebaut, darf kein Promise hängen bleiben.
  useEffect(() => () => stateRef.current?.resolve(false), []);

  const close = (value: boolean): void => {
    state?.resolve(value);
    setState(null);
  };

  const options = state?.options;
  const confirmDialog = (
    <Dialog
      open={state !== null}
      onClose={() => close(false)}
      title={options?.title ?? ''}
      footer={
        <>
          <Button variant="secondary" onClick={() => close(false)}>
            {options?.cancelLabel ?? 'Abbrechen'}
          </Button>
          <Button variant={options?.tone === 'danger' ? 'danger' : 'primary'} onClick={() => close(true)}>
            {options?.confirmLabel ?? 'Bestätigen'}
          </Button>
        </>
      }
    >
      <div className="text-sm text-slate-700">{options?.message}</div>
    </Dialog>
  );

  return { confirm, confirmDialog };
}
